import axios from 'axios'

// 动态获取API基础URL
const getApiBaseUrl = () => {
  // 始终使用当前页面的地址，确保不会跨域
  const { protocol, hostname, port } = window.location

  // 构建API基础URL
  let apiUrl = `${protocol}//${hostname}`

  // 添加端口号（只有在端口存在且不是默认端口时）
  const portNum = port ? parseInt(port, 10) : null
  const isDefaultPort = (protocol === 'http:' && portNum === 80) ||
                       (protocol === 'https:' && portNum === 443)

  if (portNum && !isDefaultPort) {
    apiUrl += `:${portNum}`
  }

  console.log('当前location:', { protocol, hostname, port, portNum })
  console.log('API基础URL:', apiUrl)

  return apiUrl
}

const API_BASE_URL = getApiBaseUrl()

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    const message = error.response?.data?.error || error.message || '请求失败'
    return Promise.reject(new Error(message))
  }
)

// 配置 API
export const getConfig = async () => {
  const response = await api.get('/config')
  return response
}

export const getConfigByType = async (type) => {
  const response = await api.get(`/config/${type}`)
  return response
}

export const updateConfig = async (type, data) => {
  const response = await api.put(`/config/${type}`, { data })
  return response
}

export const validateConfig = async (type, data) => {
  const response = await api.post(`/config/validate/${type}`, { data })
  return response
}

export const createBackup = async () => {
  const response = await api.post('/config/backup')
  return response
}

export const getBackups = async () => {
  const response = await api.get('/config/backups')
  return response
}

// 导出所有配置
export const exportAllConfigs = async () => {
  // 对于 blob 响应，我们需要直接使用 axios 而不是经过拦截器
  const response = await axios.get(`${API_BASE_URL}/api/config/export`, {
    responseType: 'blob',
    timeout: 30000
  })
  return response.data
}

// 导入配置
export const importConfigs = async (file) => {
  const formData = new FormData()
  formData.append('configFile', file)

  const response = await api.post('/config/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  return response
}

// 恢复备份
export const restoreBackup = async (backupName) => {
  const response = await api.post(`/config/restore/${backupName}`)
  return response
}

// 删除备份
export const deleteBackup = async (backupName) => {
  const response = await api.delete(`/config/backups/${backupName}`)
  return response
}

// 服务相关 API
export const addService = async (groupName, service) => {
  const response = await getConfigByType('services')
  const config = response.data

  // 查找或创建服务组
  let group = config.find(g => Object.keys(g)[0] === groupName)
  if (!group) {
    group = { [groupName]: [] }
    config.push(group)
  }

  // 添加服务
  group[groupName].push(service)

  return updateConfig('services', config)
}

export const updateService = async (groupName, serviceIndex, service) => {
  const response = await getConfigByType('services')
  const config = response.data

  const group = config.find(g => Object.keys(g)[0] === groupName)
  if (group && group[groupName][serviceIndex]) {
    group[groupName][serviceIndex] = service
  }

  return updateConfig('services', config)
}

export const deleteService = async (groupName, serviceIndex) => {
  const response = await getConfigByType('services')
  const config = response.data

  const group = config.find(g => Object.keys(g)[0] === groupName)
  if (group && group[groupName][serviceIndex]) {
    group[groupName].splice(serviceIndex, 1)

    // 如果组为空，删除组
    if (group[groupName].length === 0) {
      const groupIndex = config.indexOf(group)
      config.splice(groupIndex, 1)
    }
  }

  return updateConfig('services', config)
}

export const moveService = async (groupName, serviceIndex, direction) => {
  const response = await getConfigByType('services')
  const config = response.data

  const group = config.find(g => Object.keys(g)[0] === groupName)
  if (!group || !group[groupName]) {
    throw new Error('分组不存在')
  }

  const services = group[groupName]
  const targetIndex = direction === 'up' ? serviceIndex - 1 : serviceIndex + 1

  if (targetIndex >= 0 && targetIndex < services.length) {
    // 交换服务位置
    [services[serviceIndex], services[targetIndex]] = [services[targetIndex], services[serviceIndex]]
    return updateConfig('services', config)
  } else {
    throw new Error('无法移动到指定位置')
  }
}

export const updateGroupName = async (oldGroupName, newGroupName, layoutConfig = null) => {
  const response = await getConfigByType('services')
  const config = response.data

  const groupIndex = config.findIndex(g => Object.keys(g)[0] === oldGroupName)
  if (groupIndex === -1) {
    throw new Error('分组不存在')
  }

  // 检查新分组名是否已存在
  const existingGroup = config.find(g => Object.keys(g)[0] === newGroupName)
  if (existingGroup && newGroupName !== oldGroupName) {
    throw new Error('分组名称已存在')
  }

  // 更新分组名称
  const services = config[groupIndex][oldGroupName]
  config[groupIndex] = { [newGroupName]: services }

  // 更新服务配置
  await updateConfig('services', config)

  // 如果提供了布局配置，同时更新 settings.yaml
  if (layoutConfig) {
    try {
      const settingsResponse = await getConfigByType('settings')
      const settingsData = settingsResponse.data || {}

      if (!settingsData.layout) {
        settingsData.layout = {}
      }

      // 如果分组名称改变了，删除旧的布局配置
      if (oldGroupName !== newGroupName && settingsData.layout[oldGroupName]) {
        delete settingsData.layout[oldGroupName]
      }

      // 设置新的布局配置
      settingsData.layout[newGroupName] = layoutConfig

      await updateConfig('settings', settingsData)
      console.log(`✅ 已更新分组 "${newGroupName}" 的布局配置:`, layoutConfig)
    } catch (error) {
      console.error('❌ 更新布局配置失败:', error)
      // 不抛出错误，因为服务配置已经成功更新
    }
  }

  return { success: true }
}

// 小部件相关 API
export const addWidget = async (widget) => {
  const response = await getConfigByType('widgets')
  const config = response.data
  config.push(widget)
  return updateConfig('widgets', config)
}

export const updateWidget = async (index, widget) => {
  const response = await getConfigByType('widgets')
  const config = response.data
  if (config[index]) {
    config[index] = widget
  }
  return updateConfig('widgets', config)
}

export const deleteWidget = async (index) => {
  const response = await getConfigByType('widgets')
  const config = response.data
  if (config[index]) {
    config.splice(index, 1)
  }
  return updateConfig('widgets', config)
}

// 书签相关 API
export const addBookmarkGroup = async (groupName, bookmarks = []) => {
  const response = await getConfigByType('bookmarks')
  const config = response.data
  config.push({ [groupName]: bookmarks })
  return updateConfig('bookmarks', config)
}

export const addBookmark = async (groupName, bookmark) => {
  const response = await getConfigByType('bookmarks')
  const config = response.data

  let group = config.find(g => Object.keys(g)[0] === groupName)
  if (!group) {
    group = { [groupName]: [] }
    config.push(group)
  }

  group[groupName].push(bookmark)
  return updateConfig('bookmarks', config)
}

export const updateBookmark = async (groupName, bookmarkIndex, bookmark) => {
  const response = await getConfigByType('bookmarks')
  const config = response.data

  const group = config.find(g => Object.keys(g)[0] === groupName)
  if (group && group[groupName][bookmarkIndex]) {
    group[groupName][bookmarkIndex] = bookmark
  }

  return updateConfig('bookmarks', config)
}

export const deleteBookmark = async (groupName, bookmarkIndex) => {
  const response = await getConfigByType('bookmarks')
  const config = response.data

  const group = config.find(g => Object.keys(g)[0] === groupName)
  if (group && group[groupName][bookmarkIndex]) {
    group[groupName].splice(bookmarkIndex, 1)

    // 如果组为空，删除组
    if (group[groupName].length === 0) {
      const groupIndex = config.indexOf(group)
      config.splice(groupIndex, 1)
    }
  }

  return updateConfig('bookmarks', config)
}

// 移动书签
export const moveBookmark = async (groupName, bookmarkIndex, direction) => {
  const response = await getConfigByType('bookmarks')
  const config = response.data

  const group = config.find(g => Object.keys(g)[0] === groupName)
  if (!group || !group[groupName]) {
    throw new Error('分组不存在')
  }

  const bookmarks = group[groupName]
  const targetIndex = direction === 'up' ? bookmarkIndex - 1 : bookmarkIndex + 1

  if (targetIndex >= 0 && targetIndex < bookmarks.length) {
    // 交换书签位置
    [bookmarks[bookmarkIndex], bookmarks[targetIndex]] = [bookmarks[targetIndex], bookmarks[bookmarkIndex]]
    return updateConfig('bookmarks', config)
  } else {
    throw new Error('无法移动到指定位置')
  }
}

// 更新书签分组名称
export const updateBookmarkGroupName = async (oldGroupName, newGroupName) => {
  const response = await getConfigByType('bookmarks')
  const config = response.data

  const groupIndex = config.findIndex(g => Object.keys(g)[0] === oldGroupName)
  if (groupIndex === -1) {
    throw new Error('分组不存在')
  }

  // 检查新分组名是否已存在
  const existingGroup = config.find(g => Object.keys(g)[0] === newGroupName)
  if (existingGroup && newGroupName !== oldGroupName) {
    throw new Error('分组名称已存在')
  }

  // 更新分组名称
  const bookmarks = config[groupIndex][oldGroupName]
  config[groupIndex] = { [newGroupName]: bookmarks }

  return updateConfig('bookmarks', config)
}

// 图标相关 API
export const extractPageIcons = async (pageUrl) => {
  const response = await api.get('/icons/extract', { params: { url: pageUrl } })
  return response
}

export const downloadIconToLocal = async (iconUrl, preferredName) => {
  const response = await api.post('/icons/download', { url: iconUrl, preferredName })
  return response
}

export default api
